import React, { useRef, useState } from 'react';
import { Button as CustomButton } from '@spark/ui-components/components/Button';
import Tooltip from '@spark/ui-components/components/Tooltip';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import styled from 'styled-components';
import { formatarDataHoraBr as formatarDataHora } from '../utils/dataBr';

export const descreverSituacaoData = option => {
  if (!option?.definitiva) return 'Prévia';
  const quando = formatarDataHora(option.definitivaAplicadaEm);
  return ['Definitiva', quando ? `aplicada em ${quando}` : null, option.definitivaUsuario ? `por ${option.definitivaUsuario}` : null].filter(Boolean).join(' · ');
};

// Só as datas mais recentes viram botão; qualquer outra data importada é aberta pelo calendário (não enche a tela).
export const DATAS_VISIVEIS = 6;

const formatarDia = valor => {
  const [ano, mes, dia] = String(valor || '').split('-');
  return dia ? `${dia}/${mes}/${ano}` : '';
};

const BuscaData = ({ dateOptions, onSelectDate }) => {
  const [valor, setValor] = useState('');
  const [aviso, setAviso] = useState('');
  const inputRef = useRef(null);
  const disponiveis = dateOptions.map(option => option.value);

  const escolher = data => {
    setValor(data);
    if (!data) { setAviso(''); return; }
    if (disponiveis.includes(data)) {
      setAviso('');
      setValor('');
      onSelectDate(data);
      return;
    }
    setAviso(`Sem programação importada em ${formatarDia(data)}.`);
  };

  // Botão compacto (mesmo tamanho de ‹ › 1º 2º) que abre o calendário do navegador sobre um input de data invisível.
  const abrirCalendario = () => {
    const campo = inputRef.current;
    if (!campo) return;
    try {
      if (typeof campo.showPicker === 'function') { campo.showPicker(); return; }
    } catch {
      // Navegador sem showPicker liberado: cai no foco do campo.
    }
    campo.focus();
  };

  return (
    <BuscaCompacta>
      <Tooltip dir="top" text="Buscar data da importação">
        <ButtonWrap>
          <HeaderColorButton variant="primary" size="sm" aria-label="Buscar data da importação" onClick={abrirCalendario}>
            <CalendarMonthIcon style={{ fontSize: 16 }} />
          </HeaderColorButton>
        </ButtonWrap>
      </Tooltip>
      <DataInvisivel
        ref={inputRef}
        type="date"
        aria-label="Data da importação"
        tabIndex={-1}
        value={valor}
        min={disponiveis[disponiveis.length - 1]}
        max={disponiveis[0]}
        aria-describedby={aviso ? 'programacaoHavanBuscaDataAviso' : undefined}
        onChange={event => escolher(event.target.value)}
      />
      {aviso ? <AvisoBusca id="programacaoHavanBuscaDataAviso" role="status">{aviso}</AvisoBusca> : null}
    </BuscaCompacta>
  );
};

const ProgramacaoHavanDateFilter = ({
  dateOptions,
  selectedDate,
  turnoAtivo,
  onSelectDate,
  onPreviousDate,
  onNextDate,
  onTurnoFilter,
  onAdicionarCarga,
  onAbrirLegenda,
  onBaixarExcel,
  // Botões a mais da tela que usa o filtro (ex.: visões da emissão de protocolo): { chave, rotulo, icone, ativo, onClick }.
  botoesExtras = []
}) => {
  const selecionada = dateOptions.find(option => option.value === selectedDate);
  const recentes = dateOptions.slice(0, DATAS_VISIVEIS);
  // Data antiga escolhida pela busca: aparece como botão ativo enquanto estiver selecionada.
  const botoes = selecionada && !recentes.includes(selecionada) ? [...recentes, selecionada] : recentes;

  return (
    <FilterRow>
      <DateButtons>
        {botoes.map(option => (
          <DateFilterButton
            key={option.value}
            variant="outline-secondary"
            $active={option.value === selectedDate}
            size="sm"
            title={descreverSituacaoData(option)}
            onClick={() => onSelectDate(option.value)}
          >
            {option.definitiva ? <MarcaDefinitiva aria-hidden="true" /> : null}
            {option.label}
          </DateFilterButton>
        ))}

        {selecionada ? (
          <SeloSituacao data-testid="situacao-data" $definitiva={selecionada.definitiva}>
            {descreverSituacaoData(selecionada)}
          </SeloSituacao>
        ) : null}
      </DateButtons>
  
      <TurnoButtons>
        {onAdicionarCarga ? (
          <Tooltip dir="top" text="Adicionar carga manualmente nesta data">
            <ButtonWrap>
              <HeaderColorButton variant="primary" size="sm" aria-label="Adicionar carga manualmente" disabled={!selectedDate} onClick={onAdicionarCarga}>
                <AddIcon style={{ fontSize: 16 }} />
              </HeaderColorButton>
            </ButtonWrap>
          </Tooltip>
        ) : null}
        {onAbrirLegenda ? (
          <Tooltip dir="top" text="O que cada cor e cada tag quer dizer">
            <ButtonWrap>
              <HeaderColorButton variant="primary" size="sm" aria-label="Legenda das cores e tags" onClick={onAbrirLegenda}>
                <InfoOutlinedIcon style={{ fontSize: 16 }} />
              </HeaderColorButton>
            </ButtonWrap>
          </Tooltip>
        ) : null}
        {onBaixarExcel ? (
          <Tooltip dir="top" text="Baixar Excel do dia (os dois turnos)">
            <ButtonWrap>
              <HeaderColorButton variant="primary" size="sm" aria-label="Baixar Excel do dia" disabled={!selectedDate} onClick={onBaixarExcel}>
                <FileDownloadOutlinedIcon style={{ fontSize: 16 }} />
              </HeaderColorButton>
            </ButtonWrap>
          </Tooltip>
        ) : null}
        {botoesExtras.map(botao => (
          <Tooltip key={botao.chave} dir="top" text={botao.rotulo}>
            <ButtonWrap>
              <HeaderColorButton variant="primary" size="sm" aria-label={botao.rotulo} $active={botao.ativo} aria-pressed={Boolean(botao.ativo)} onClick={botao.onClick}>
                {botao.icone}
              </HeaderColorButton>
            </ButtonWrap>
          </Tooltip>
        ))}
        <BuscaData dateOptions={dateOptions} onSelectDate={onSelectDate} />
        <Tooltip dir="top" text="Data anterior">
          <ButtonWrap>
            <HeaderColorButton
              variant="primary"
              size="sm"
              aria-label="Data anterior"
              onClick={onPreviousDate}
            >
              <ButtonArrow>‹</ButtonArrow>
            </HeaderColorButton>
          </ButtonWrap>
        </Tooltip>
        <Tooltip dir="top" text="Próxima data">
          <ButtonWrap>
            <HeaderColorButton
              variant="primary"
              size="sm"
              aria-label="Próxima data"
              onClick={onNextDate}
            >
              <ButtonArrow>›</ButtonArrow>
            </HeaderColorButton>
          </ButtonWrap>
        </Tooltip>
        <ButtonWrap>
          <HeaderColorButton
            variant="primary"
            size="sm"
            $active={turnoAtivo === 'primeiro'}
            aria-pressed={turnoAtivo === 'primeiro'}
            title="Filtrar primeiro turno"
            onClick={() => onTurnoFilter('primeiro')}
          >
            1º
          </HeaderColorButton>
        </ButtonWrap>
        <ButtonWrap>
          <HeaderColorButton
            variant="primary"
            size="sm"
            $active={turnoAtivo === 'segundo'}
            aria-pressed={turnoAtivo === 'segundo'}
            title="Filtrar segundo turno"
            onClick={() => onTurnoFilter('segundo')}
          >
            2º
          </HeaderColorButton>
        </ButtonWrap>
      </TurnoButtons>
    </FilterRow>
  );
};

export default ProgramacaoHavanDateFilter;

const MarcaDefinitiva = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-radius: 50%;
  background: #2e9b4f;
  vertical-align: middle;
`;

const SeloSituacao = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border: 1px solid ${({ $definitiva }) => ($definitiva ? '#9fd3ad' : '#cbd5e1')};
  border-radius: 999px;
  background: ${({ $definitiva }) => ($definitiva ? '#e8f6ec' : '#f1f5f9')};
  color: ${({ $definitiva }) => ($definitiva ? '#1f5c30' : '#526275')};
  font-size: 12px;
  font-weight: 600;
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const DateButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TurnoButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const HEADER_BLUE = '#163455';
const COMPACT_BUTTON_SIZE = '26px';

const DateFilterButton = styled(CustomButton)`
  border-color: ${HEADER_BLUE} !important;
  background: ${({ $active }) => ($active ? HEADER_BLUE : '#eef3f8')} !important;
  color: ${({ $active }) => ($active ? '#ffffff' : HEADER_BLUE)} !important;

  &:hover,
  &:focus {
    border-color: ${HEADER_BLUE} !important;
    background: ${HEADER_BLUE} !important;
    color: #ffffff !important;
  }
`;

const HeaderColorButton = styled(CustomButton)`
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: ${COMPACT_BUTTON_SIZE} !important;
  width: ${COMPACT_BUTTON_SIZE};
  height: ${COMPACT_BUTTON_SIZE};
  padding: 0 !important;
  border-color: ${HEADER_BLUE} !important;
  background: ${({ $active }) => ($active ? '#0f2743' : HEADER_BLUE)} !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  box-shadow: ${({ $active }) => ($active ? '0 0 0 2px rgba(22, 52, 85, 0.25)' : 'none')};

  &:hover,
  &:focus {
    border-color: ${HEADER_BLUE} !important;
    background: #0f2743 !important;
    color: #ffffff !important;
  }
`;

const ButtonWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${COMPACT_BUTTON_SIZE};
  height: ${COMPACT_BUTTON_SIZE};
`;

const ButtonArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  transform: translateY(-1px);
`;


const AvisoBusca = styled.span`
  position: absolute;
  z-index: 5;
  top: calc(100% + 4px);
  right: 0;
  padding: 4px 8px;
  border: 1px solid #f0a7ad;
  border-radius: 4px;
  background: #fff4f5;
  color: #a61e2a;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const BuscaCompacta = styled.span`position: relative; display: inline-flex;`;

// Fica atrás do botão: só serve para abrir o calendário nativo e receber a data escolhida.
const DataInvisivel = styled.input`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
`;
